import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    // showAddToList: boolean;
    showAvailability: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    settingsCollection: getSettingsCollection(state),
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    availabilityStyles?: ProductAvailabilityStyles;
    nonPurshsedStyles?: InjectableCss;
    lowStockStyles?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
            span {
                letter-spacing: 2px;
                font-size: 12px;
                font-weight: 600;
                text-transform: uppercase;
                color: #ffffff;
            }
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 3rem;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
    nonPurshsedStyles: {
        css: css`
            font-size: 16px;
            font-weight: 600;
        `,
    },
    lowStockStyles: {
        css: css`
            padding-top: 8px;
            span {
                font-size: 15px;
                font-weight: 400;
                color: rgb(230, 78, 37);
            }
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    settingsCollection,
    showAvailability,
}) => {
    const discontinuedProduct = productContext.product.properties?.isObsolete?.toLowerCase() === "true";

    let lowStockInventory;

    if (
        productContext.productInfo.inventory?.qtyOnHand &&
        productContext.productInfo.inventory.qtyOnHand > 0 &&
        productContext.productInfo.inventory?.qtyOnHand < productContext.productInfo.qtyOrdered
    ) {
        lowStockInventory = true;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {!productContext.product.cantBuy && !discontinuedProduct && showPrice && (
                <>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            {!productContext.product.cantBuy &&
                settingsCollection.productSettings.showInventoryAvailability &&
                !discontinuedProduct &&
                showAvailability && <ProductContextAvailability extendedStyles={styles.availabilityStyles} />}

            {lowStockInventory && !productContext.product.cantBuy && (
                <StyledWrapper {...styles.lowStockStyles}>
                    <Typography>{siteMessage("Low_Stock_Message")}</Typography>
                </StyledWrapper>
            )}
            {!discontinuedProduct && (
                <StyledWrapper {...styles.addToCartWrapper}>
                    <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                    <ProductAddToCartButton
                        data-test-selector={`actionsAddToCart${productContext.product.id}`}
                        extendedStyles={styles.addToCartButton}
                    />
                </StyledWrapper>
            )}
            {productContext.product.cantBuy && (
                <StyledWrapper data-test-selector="productNotPurchased">
                    <Typography {...styles.nonPurshsedStyles}>{siteMessage("Product_Non_Purchasable")}</Typography>
                </StyledWrapper>
            )}
            {discontinuedProduct && (
                <StyledWrapper data-test-selector="productDiscontinued">
                    <Typography
                        css={css`
                            color: #b31515;
                            font-weight: 600;
                            font-size: 16px;
                        `}
                    >
                        {siteMessage("Discontinued_ProductMeassge")}
                    </Typography>
                </StyledWrapper>
            )}
            {/* {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )} */}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
